import { Badge, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core'
import { IconArrowRight, IconPlayerPause } from '@tabler/icons-react'
import type { CaminhoDoFluxo, MapaDoFluxo, NumerosDoFluxo } from '@/api/chamados'
import { corDoStatus } from '@/components/chamados/StatusBadge'
import { ROTULO_DO_ATOR, chamados, condicaoLegivel, rotuloDoEstado } from './textosDoFluxo'
import { saidasDe, type FluxoVisivel, type Visao } from './visaoDoFluxo'
import type { Selecao } from './DetalheDoFluxo'

interface Props {
  mapa: MapaDoFluxo
  fluxo: FluxoVisivel
  numeros?: NumerosDoFluxo
  visao: Visao
  onSelecionar: (s: Selecao) => void
}

/**
 * Os estados em ordem, cada um com as saídas por extenso. É o padrão abaixo de `md` (o diagrama
 * não cabe em 360 px) e o modo que leitor de tela usa.
 */
export function ListaDoFluxo({ mapa, fluxo, numeros, visao, onSelecionar }: Props) {
  return (
    <Stack gap="sm" component="ol" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {fluxo.estados.map((estado) => {
        const saidas = saidasDe(estado, fluxo.caminhos, mapa.encerrados)
        const atalho = fluxo.atalhos.find((a) => a.de === estado)
        const agora = numeros?.por_estado[estado] ?? 0
        const encerra = mapa.encerrados.includes(estado)
        return (
          <Paper key={estado} component="li" withBorder p="md" radius="md"
            style={encerra ? { borderStyle: 'double', borderWidth: 3 } : undefined}>
            <Group justify="space-between" wrap="wrap" gap="xs">
              <UnstyledButton onClick={() => onSelecionar({ tipo: 'estado', estado })}
                aria-label={`Detalhes de ${rotuloDoEstado(estado, visao)}`}>
                <Group gap="xs" wrap="nowrap">
                  <Badge color={corDoStatus(estado)} variant="light" size="lg" radius="sm">
                    {rotuloDoEstado(estado, visao)}
                  </Badge>
                  {mapa.pausam.includes(estado) && (
                    <IconPlayerPause size={16} aria-label="pausa o prazo" stroke={1.75} />
                  )}
                </Group>
              </UnstyledButton>
              {agora > 0 && (
                <Text size="sm" ff="monospace">{chamados(agora)} agora</Text>
              )}
            </Group>
            {encerra && (
              <Text size="sm" mt={6} style={{ color: 'var(--ai-text-secondary)' }}>
                Encerra o atendimento{saidas.length > 0 ? ', e pode reabrir' : ''}.
              </Text>
            )}
            {saidas.length > 0 && (
              <Stack gap={4} mt="xs" component="ul" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {saidas.map((c, i) => (
                  <Saida key={i} caminho={c} visao={visao} onClick={() => onSelecionar({ tipo: 'caminho', caminho: c })} />
                ))}
              </Stack>
            )}
            {atalho && (
              <Text size="sm" mt="xs" style={{ color: 'var(--ai-text-secondary)' }}>
                Segue na {atalho.aba === 'mudanca' ? 'esteira de mudança por ambiente' : 'aba de atendimento'}.
              </Text>
            )}
          </Paper>
        )
      })}
    </Stack>
  )
}

function Saida({ caminho, visao, onClick }: { caminho: CaminhoDoFluxo; visao: Visao; onClick: () => void }) {
  return (
    <li>
      <UnstyledButton onClick={onClick} style={{ width: '100%', textAlign: 'left' }}>
        <Group gap={6} wrap="nowrap" align="flex-start">
          <IconArrowRight size={16} stroke={1.75} style={{ flexShrink: 0, marginTop: 3 }} />
          <Text size="sm">
            <Text span fw={600}>{rotuloDoEstado(caminho.para, visao)}</Text>
            {' · '}
            {condicaoLegivel(caminho.condicao)}
            <Text span size="xs" style={{ color: 'var(--ai-text-secondary)' }}>
              {' '}({ROTULO_DO_ATOR[caminho.ator].toLowerCase()})
            </Text>
          </Text>
        </Group>
      </UnstyledButton>
    </li>
  )
}
